import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/auth/AuthProvider";
import type { WizardStepContext } from "@/base";
import { messageFor } from "@/lib/errors";
import {
  carrierStepError,
  getCarrierQuestions,
  getSubmissionCarriers,
  missingFieldsOf,
  requestPriceIndication,
} from "@/lib/submissions/brokerSubmissions";
import {
  carriersFrom,
  withIndications,
  withQuestionSet,
  withQuestionsProblem,
  type WcCarrier,
  type WcCarrierStep,
} from "@/lib/submissions/wc/carrierStep";
import type { WcDraft } from "@/lib/submissions/wc/draft";

const OPENING: WcCarrierStep = {
  carriers: null,
  problem: null,
  fanOutProblem: null,
};

/**
 * The last step's reads (BRK-S393), each time it opens. First the carriers PMS
 * has in play, from its database alone; then the fan-out and every API
 * carrier's questions, all at once (PMS-S815: one carrier's leg runs one
 * caller at a time, so the parallel reads are safe). Each lands as it comes,
 * through `setLoaded`, so only the broker's answers read as unsaved changes;
 * a re-opened step reads afresh, so an earlier step's change is sent.
 */
export function useCarrierStep(
  draft: WcDraft,
  setLoaded: WizardStepContext<WcDraft>["setLoaded"],
): {
  /** Read the carriers again, after their read failed. */
  retry: () => void;
  /** Ask the markets again, after the fan-out failed as a whole. */
  retryMarkets: () => void;
  /** Read one carrier's questions again. */
  readQuestions: (carrierId: string) => void;
} {
  const { authedRequest } = useAuth();
  const id = draft.submission?.id ?? null;
  // Late answers land only while the step is open.
  const alive = useRef(true);

  const put = useCallback(
    (update: (step: WcCarrierStep) => WcCarrierStep) => {
      if (!alive.current) return;
      setLoaded((d) => ({
        ...d,
        carrierStep: update(d.carrierStep ?? OPENING),
      }));
    },
    [setLoaded],
  );
  const putCarriers = useCallback(
    (update: (carriers: WcCarrier[]) => WcCarrier[]) =>
      put((s) => (s.carriers ? { ...s, carriers: update(s.carriers) } : s)),
    [put],
  );

  const readQuestions = useCallback(
    async (carrierId: string) => {
      if (!id) return;
      putCarriers((cs) => withQuestionsProblem(cs, carrierId, null));
      try {
        const set = await getCarrierQuestions(authedRequest, id, carrierId);
        putCarriers((cs) => withQuestionSet(cs, carrierId, set));
      } catch (err) {
        putCarriers((cs) =>
          withQuestionsProblem(cs, carrierId, carrierStepError(err)),
        );
      }
    },
    [authedRequest, id, putCarriers],
  );

  /** The fan-out. Its legs are kept for the quotes screen; until PMS-S901,
   *  it is also what lets Submit record Great American's quote request. */
  const fanOut = useCallback(async () => {
    if (!id) return;
    putCarriers((cs) =>
      cs.map((c) =>
        c.quotingMode === "api" ? { ...c, indication: undefined } : c,
      ),
    );
    put((s) => ({ ...s, fanOutProblem: null }));
    try {
      const { results } = await requestPriceIndication(authedRequest, id);
      putCarriers((cs) => withIndications(cs, results));
    } catch (err) {
      put((s) => ({
        ...s,
        fanOutProblem: {
          message: messageFor(err),
          missing: missingFieldsOf(err),
        },
        carriers: s.carriers && withIndications(s.carriers, []),
      }));
    }
  }, [authedRequest, id, put, putCarriers]);

  const open = useCallback(async () => {
    if (!id) return;
    put(() => OPENING);
    let carriers: WcCarrier[];
    try {
      carriers = carriersFrom(await getSubmissionCarriers(authedRequest, id));
    } catch (err) {
      put((s) => ({ ...s, problem: messageFor(err) }));
      return;
    }
    put((s) => ({ ...s, carriers }));
    const online = carriers.filter((c) => c.quotingMode === "api");
    if (online.length > 0) void fanOut();
    for (const c of online) void readQuestions(c.carrierId);
  }, [authedRequest, id, put, fanOut, readQuestions]);

  // Once per opening of the step. A ref guards it, not the cleanup, so a
  // development re-mount never sends the application twice.
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    alive.current = true;
    if (id && openedFor.current !== id) {
      openedFor.current = id;
      void open();
    }
    return () => {
      alive.current = false;
    };
  }, [id, open]);

  return {
    retry: () => void open(),
    retryMarkets: () => void fanOut(),
    readQuestions: (carrierId) => void readQuestions(carrierId),
  };
}
